import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { pixel } from "@/lib/metaPixel";
import PrivateRoute from "@/components/PrivateRoute";
import AdminPrivateRoute from "@/components/AdminPrivateRoute";
import Landing from "@/pages/Landing";
import Signin from "@/pages/Signin";
import Welcome from "@/pages/Welcome";
import Onboard from "@/pages/Onboard";
import AppShell from "@/pages/AppShell";
import Chat from "@/pages/Chat";
import Dashboard from "@/pages/Dashboard";
import Debtors from "@/pages/Debtors";
import Inventory from "@/pages/Inventory";
import Loans from "@/pages/Loans";
import Documents from "@/pages/Documents";
import Reminders from "@/pages/Reminders";
import Profile from "@/pages/Profile";
import Pricing from "@/pages/Pricing";
import Plans from "@/pages/Plans";
import DeleteAccountPublic from "@/pages/DeleteAccountPublic";
import MagicLink from "@/pages/MagicLink";
import WhatsAppSimulator from "@/pages/WhatsAppSimulator";
import PublicPay from "@/pages/PublicPay";
import PaymentSuccess from "@/pages/PaymentSuccess";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminShell from "@/pages/admin/AdminShell";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminLoans from "@/pages/admin/AdminLoans";
import AdminTransactions from "@/pages/admin/AdminTransactions";
import AdminReferrals from "@/pages/admin/AdminReferrals";
import AdminScheduler from "@/pages/admin/AdminScheduler";
import AdminCron from "@/pages/admin/AdminCron";
import AdminSupport from "@/pages/admin/AdminSupport";
import AdminWallet from "@/pages/admin/AdminWallet";
import AdminDocuments from "@/pages/admin/AdminDocuments";
import AdminTools from "@/pages/admin/AdminTools";
import AdminReminders from "@/pages/admin/AdminReminders";

function App() {
  useEffect(() => {
    document.title = "Kobolog AI — Every kobo, logged.";
  }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors closeButton />
        <PixelRouteTracker />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/signin" element={<Signin />} />
          <Route path="/delete-account" element={<DeleteAccountPublic />} />
          <Route
            path="/onboard"
            element={
              <PrivateRoute requireOnboarded={false}>
                <Onboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/welcome"
            element={
              <PrivateRoute>
                <Welcome />
              </PrivateRoute>
            }
          />
          <Route
            path="/app"
            element={
              <PrivateRoute>
                <AppShell />
              </PrivateRoute>
            }
          >
            <Route index element={<Navigate to="/app/dashboard" replace />} />
            <Route path="chat" element={<Chat />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="debtors" element={<Debtors />} />
            <Route path="inventory" element={<Inventory />} />
            <Route path="loans" element={<Loans />} />
            <Route path="documents" element={<Documents />} />
            <Route path="reminders" element={<Reminders />} />
            <Route path="profile" element={<Profile />} />
            <Route path="pricing" element={<Pricing />} />
            <Route path="plans" element={<Plans />} />
            <Route path="whatsapp" element={<WhatsAppSimulator />} />
          </Route>
          <Route path="/magic" element={<MagicLink />} />
          {/* Public payment page — anyone can pay a Kobolog trader. Primary: /pay/:tag.
              We also accept /@:tag but React Router v6 needs a different recognized form,
              so we register both `@:tag` (slug starts with @) and the cleaner /pay/:tag. */}
          <Route path="/pay/:tag" element={<PublicPay />} />
          <Route path="/u/:tag" element={<PublicPay />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          {/* Admin routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminPrivateRoute><AdminShell /></AdminPrivateRoute>}>
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="loans" element={<AdminLoans />} />
            <Route path="transactions" element={<AdminTransactions />} />
            <Route path="referrals" element={<AdminReferrals />} />
            <Route path="scheduler" element={<AdminScheduler />} />
            <Route path="cron" element={<AdminCron />} />
            <Route path="support" element={<AdminSupport />} />
            <Route path="wallet" element={<AdminWallet />} />
            <Route path="documents" element={<AdminDocuments />} />
            <Route path="reminders" element={<AdminReminders />} />
            <Route path="tools" element={<AdminTools />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;

/**
 * Fires a Meta Pixel PageView on every SPA route change.
 * The base pixel in index.html already fires PageView on initial page load, so
 * this hook only re-fires when the pathname actually changes.
 */
function PixelRouteTracker() {
  const location = useLocation();
  useEffect(() => {
    pixel.pageView();
  }, [location.pathname]);
  return null;
}
